import type { CrosschainBuyViewState } from '@/features/crosschain-buy/buy-flow/view-state'
import type { StepStatus as PersistedStepStatus } from '@/features/crosschain-buy/domain/progress'
import {
  hasUsdtToTonCompleted,
  isTonToSpottyInFlight,
} from '@/features/crosschain-buy/domain/ton-progress'
import type { StepStatus } from './types'

export interface StepStatuses {
  step1: StepStatus
  step2: StepStatus
  step3: StepStatus
}

// Status classification sets for computeStepStatuses
const STEP1_ACTIVE_STATUSES = new Set([
  'bridge_approving',
  'bridge_swapping',
  'bridge_tracking',
  'solana_jupiter_swapping',
  'solana_layerswap_bridging',
  'intermediate_symbiosis_approving',
  'intermediate_symbiosis_swapping',
  'intermediate_symbiosis_tracking',
])
const STEP1_DONE_STATUSES = new Set(['destination_swap_ready', 'destination_swapping', 'success'])
const STEP1_FAIL_ERRORS = new Set([
  'approval_rejected',
  'swap_rejected',
  'bridge_failed',
  'swap_timeout',
  'solana_swap_failed',
  'solana_tx_failed',
  'wrong_chain',
])
const STEP1_COMPLETED_ON_ERROR = new Set([
  'ton_tx_failed',
  'ton_tx_uncertain',
  'ton_sync_required',
  'ston_simulation_failed',
])
const STEP2_USDT_TO_TON_SUBSTATUS = new Set(['usdt_to_ton', 'settling'])

export function computeStep1(
  status: string,
  lastError: string | null,
  tonLegSubStatus: CrosschainBuyViewState['tonLegSubStatus'],
  persistedBridgeStatus?: PersistedStepStatus | null
): StepStatus {
  if (persistedBridgeStatus === 'completed') return 'completed'
  if (persistedBridgeStatus === 'failed') return 'failed'
  if (['active', 'submitted', 'reconciling'].includes(persistedBridgeStatus ?? '')) return 'active'
  if (STEP1_ACTIVE_STATUSES.has(status)) return 'active'
  if (STEP1_DONE_STATUSES.has(status)) return 'completed'
  if (status === 'error') {
    if (tonLegSubStatus || STEP1_COMPLETED_ON_ERROR.has(lastError ?? '')) return 'completed'
    if (STEP1_FAIL_ERRORS.has(lastError ?? '')) return 'failed'
  }
  return 'pending'
}

export function computeStep2(
  status: string,
  tonLegSubStatus: CrosschainBuyViewState['tonLegSubStatus'],
  step1: StepStatus,
  provider: string | undefined,
  _tonLegUsdtToTonTxHash: string | null | undefined,
  tonLegUsdtToTonStepStatus: StepStatus | null | undefined,
  bridgeOutputToken?: string
): StepStatus {
  if (step1 !== 'completed') return 'pending'
  if (status === 'success') return 'completed'

  if (tonLegUsdtToTonStepStatus === 'failed') return 'failed'
  if (tonLegUsdtToTonStepStatus === 'active') return 'active'

  // A tx hash only proves the USDT→TON external transaction was broadcast.
  // The step is completed only after the service marks settlement complete;
  // otherwise a failed STON swap can show a false green check.
  if (tonLegUsdtToTonStepStatus === 'completed') return 'completed'

  // Rango and native-TON Layerswap deliver TON → step 2 is the single STON.fi hop.
  if (provider === 'rango' || (provider === 'layerswap' && bridgeOutputToken !== 'USDT')) {
    if (status === 'destination_swapping') return 'active'
    if (status === 'error' && tonLegSubStatus) return 'failed'
    return 'pending'
  }
  // Symbiosis: step 2 is USDT → TON (usdtToTon)
  const usdtToTonDone = hasUsdtToTonCompleted(tonLegSubStatus)
  if (status === 'destination_swapping' && STEP2_USDT_TO_TON_SUBSTATUS.has(tonLegSubStatus ?? ''))
    return 'active'
  if (status === 'destination_swapping' && usdtToTonDone) return 'completed'
  if (status === 'error' && usdtToTonDone) return 'completed'
  if (status === 'error' && STEP2_USDT_TO_TON_SUBSTATUS.has(tonLegSubStatus ?? '')) return 'failed'
  return 'pending'
}

export function computeStep3(
  status: string,
  tonLegSubStatus: CrosschainBuyViewState['tonLegSubStatus']
): StepStatus {
  if (status === 'success') return 'completed'
  // Step 3 active only when tonToSpotty is actually firing, NOT during the
  // awaiting-Continue-tap gap (TON_TO_SPOTTY_PENDING). hasUsdtToTonCompleted
  // returns true for both, so use the narrower isTonToSpottyInFlight here.
  if (status === 'destination_swapping' && isTonToSpottyInFlight(tonLegSubStatus)) return 'active'
  if (status === 'error' && isTonToSpottyInFlight(tonLegSubStatus)) return 'failed'
  return 'pending'
}

export function computeStepStatuses(
  status: string,
  lastError: string | null,
  tonLegSubStatus: CrosschainBuyViewState['tonLegSubStatus'],
  provider: string | undefined,
  tonLegUsdtToTonTxHash: string | null | undefined,
  tonLegUsdtToTonStepStatus: StepStatus | null | undefined,
  bridgeOutputToken?: string,
  persistedBridgeStatus?: PersistedStepStatus | null
): StepStatuses {
  const step1 =
    provider === 'layerswap' && persistedBridgeStatus === 'pending'
      ? 'pending'
      : computeStep1(status, lastError, tonLegSubStatus, persistedBridgeStatus)
  return {
    step1,
    step2: computeStep2(
      status,
      tonLegSubStatus,
      step1,
      provider,
      tonLegUsdtToTonTxHash,
      tonLegUsdtToTonStepStatus,
      bridgeOutputToken
    ),
    step3: computeStep3(status, tonLegSubStatus),
  }
}
